// Pacote 42 (PR #70) — Tela de lock que cobre o app quando o user tem
// Face ID habilitado e o app está bloqueado (post-launch ou pós-background).
//
// Comportamento:
//   - Auto-dispara o prompt ao montar (1ª vez) — UX iOS típica.
//   - Botão "Tentar de novo" se cancelar.
//   - Botão "Sair" pra logout completo (caso Face ID não funcione).
import { useEffect, useState } from "react"
import { Lock, LogOut, RotateCcw } from "lucide-react"
import { useBiometricLock } from "@/hooks/useBiometricLock"
import { useAuth } from "@/hooks/useAuth"
import { BrandMark } from "@/components/BrandMark"

export function BiometricLockGate({ children }: { children: React.ReactNode }) {
  const bio = useBiometricLock()
  const { logout, user } = useAuth()
  const [trying, setTrying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Auto-dispara o prompt 1x quando a tela aparece (entrada na sessão ou
  // resume do background). Sem isso o user precisa tocar pra começar — UX
  // mais fluida com auto-trigger.
  useEffect(() => {
    if (!user) return // não trava login screen
    if (bio.platform === "web") return // web nunca trava
    if (!bio.enabled) return
    if (bio.unlocked) return
    void doAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, bio.enabled, bio.unlocked, bio.platform])

  async function doAuth() {
    setTrying(true)
    setError(null)
    const ok = await bio.authenticate()
    setTrying(false)
    if (!ok) {
      setError("Não consegui te identificar. Tenta de novo.")
    }
  }

  // Renderização condicional:
  //   1. Web → sempre passa direto.
  //   2. Sem user logado → passa direto (deixa a Login screen aparecer).
  //   3. Lock desligado → passa direto.
  //   4. Já desbloqueou nesta sessão → passa direto.
  //   5. Caso contrário → mostra a tela de lock.
  const shouldGate =
    bio.platform !== "web" && user && bio.enabled && !bio.unlocked

  if (!shouldGate) return <>{children}</>

  return (
    <div className="fixed inset-0 z-50 bg-bg flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm flex flex-col items-center text-center gap-6">
        <BrandMark size={80} />

        <div>
          <h1 className="text-2xl font-bold tracking-tight">Finanças R e H</h1>
          <p className="text-sm text-muted mt-1">
            Toque pra desbloquear com Face ID
          </p>
        </div>

        <div className="w-16 h-16 rounded-full bg-accent/10 border-2 border-accent/30 flex items-center justify-center">
          <Lock className="w-7 h-7 text-accent" />
        </div>

        {error && (
          <p className="text-xs text-danger bg-danger/5 border border-danger/30 rounded-lg p-2 w-full">
            {error}
          </p>
        )}

        <button
          onClick={doAuth}
          disabled={trying}
          className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-accent text-accentFg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          <RotateCcw className={`w-4 h-4 ${trying ? "animate-spin" : ""}`} />
          {trying ? "Verificando..." : "Desbloquear"}
        </button>

        <button
          onClick={() => logout()}
          className="text-xs text-muted hover:text-fg inline-flex items-center gap-1.5"
        >
          <LogOut className="w-3.5 h-3.5" />
          Sair da conta
        </button>
      </div>
    </div>
  )
}
